import { Grid, Slider, Paper, Typography } from '@mui/material';
import { SCUIContent } from '../../atomic/organisms';
import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SCUIButton } from '../../atomic/atoms';
import { ShippingPerformanceQuery } from '../../services/query';
import { ShippingPerformanceCommand } from '../../services/command';

import Swal from 'sweetalert2';
import TimeHelper from '../../utils/time';
import { SPERF, sperfCls } from './shippingPerformance.classes';

export const ShippingPerformance: FC<any> = () => {
    const { t } = useTranslation();
    const [value, setValue] = useState<any>();
    const [sliderValue, setSliderValue] = useState<any>();
    const handleChange = (event: Event, newValue: any) => {
        setSliderValue(newValue);
    };

    useEffect(() => {
        getShippingPerformance();
    }, []);
    const getShippingPerformance = () => {
        ShippingPerformanceQuery.GetShippingPerformance().then((res: any) => {
            if (res?.data?.isSuccess) {
                setValue(res.data.costPerformanceBar);
                setSliderValue(res.data.costPerformanceBar.performanceWeight);
            }
        });
    };
    const updateShipping = async () => {
        Swal.fire({
            text: t('general.sureText'),
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('general.complete'),
        }).then((result: any) => {
            if (result.isConfirmed) {
                let params: any = {};
                params.performanceWeight = sliderValue;
                params.costWeight = 100 - sliderValue;
                ShippingPerformanceCommand.PostShippingPerformanceUpdate(params).then(() => {
                    Swal.fire(t('general.completed'), ' ', 'success');
                    getShippingPerformance();
                });
            }
        });
    };
    return (
        <SCUIContent className={SPERF.page}>
            <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
                className={SPERF.root}
            >
                <Grid
                    className={SPERF.header}
                    size={{
                        xs: 12,
                    }}
                >
                    <Typography sx={{ mb: 3, fontWeight: 600 }} className={SPERF.title}>
                        <h3>{t('shippingPerformance.title')}</h3>
                    </Typography>
                </Grid>
                <Grid
                    className={SPERF.controls}
                    sx={{ marginTop: '5rem' }}
                    size={{
                        xs: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                    }}
                >
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        className={SPERF.controlsRow}
                    >
                        <Grid
                            className={SPERF.performanceValueBox}
                            size={{
                                xs: 12,
                                md: 12,
                                lg: 1,
                                xl: 1,
                            }}
                        >
                            <Typography
                                variant="body1"
                                className={SPERF.performanceValue}
                                data-performance-weight={
                                    typeof sliderValue === 'number' ? sliderValue : value?.performanceWeight
                                }
                            >
                                {t('shippingPerformance.performance')}:{' '}
                                <b>{typeof sliderValue === 'number' ? sliderValue : value?.performanceWeight}</b>
                            </Typography>
                        </Grid>
                        <Grid
                            className={SPERF.sliderBox}
                            size={{
                                xs: 12,
                                md: 12,
                                lg: 9,
                                xl: 9,
                            }}
                        >
                            <Slider
                                className={SPERF.inputSlider}
                                data-performance-weight={
                                    typeof sliderValue === 'number' ? sliderValue : (value?.performanceWeight ?? 0)
                                }
                                min={0}
                                max={100}
                                value={typeof sliderValue === 'number' ? sliderValue : (value?.performanceWeight ?? 0)}
                                aria-label="Default"
                                valueLabelDisplay="on"
                                onChange={handleChange}
                                step={1}
                                sx={{ color: '#1976d2', height: 8 }}
                            />
                        </Grid>
                        <Grid className={SPERF.costValueBox}>
                            <Typography
                                variant="body1"
                                className={SPERF.costValue}
                                data-cost-weight={
                                    typeof sliderValue === 'number'
                                        ? 100.0 - sliderValue
                                        : 100.0 - value?.performanceWeight
                                }
                            >
                                {t('shippingPerformance.cost')}:{' '}
                                <b>
                                    {typeof sliderValue === 'number'
                                        ? 100.0 - sliderValue
                                        : 100.0 - value?.performanceWeight}
                                </b>
                            </Typography>
                        </Grid>
                        <Grid className={SPERF.actionBox}>
                            <SCUIButton
                                className={SPERF.btnSave}
                                onClick={updateShipping}
                                text={t('shippingPerformance.save')}
                                type="primary"
                                buttonType="submit"
                                sizeType="fixed"
                                size={'m'}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    className={SPERF.summary}
                    sx={{ mt: 4 }}
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 4,
                        xl: 3,
                    }}
                >
                    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }} className={SPERF.summaryCard}>
                        <Grid container spacing={1} className={SPERF.summaryGrid}>
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" className={SPERF.summaryLabel}>
                                    {t('shippingPerformance.costRate')}
                                </Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="error.main"
                                    align="right"
                                    className={sperfCls(SPERF.summaryValue, SPERF.summaryCostRate)}
                                    data-cost-weight={value?.costWeight}
                                >
                                    {value?.costWeight}
                                </Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" className={SPERF.summaryLabel}>
                                    {t('shippingPerformance.performanceRate')}
                                </Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    align="right"
                                    className={sperfCls(SPERF.summaryValue, SPERF.summaryPerformanceRate)}
                                    data-performance-weight={value?.performanceWeight}
                                >
                                    {value?.performanceWeight}
                                </Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" className={SPERF.summaryLabel}>
                                    {t('shippingPerformance.updatedBy')}
                                </Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    align="right"
                                    className={sperfCls(SPERF.summaryValue, SPERF.summaryUpdatedBy)}
                                    data-changed-by={value?.changedBy}
                                >
                                    {value?.changedBy}
                                </Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" className={SPERF.summaryLabel}>
                                    {t('shippingPerformance.updatedDate')}
                                </Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    align="right"
                                    className={sperfCls(SPERF.summaryValue, SPERF.summaryUpdatedDate)}
                                    data-changed-on={value?.changeOn}
                                >
                                    {value?.changeOn ? TimeHelper.formatDateHour(value?.changeOn) : '-'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </SCUIContent>
    );
};
